import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import { API } from '../config';

const PERIODI = [7, 30, 90];
const TIPO_LABEL = { staff: 'Staff', link: 'Link', agente: 'Agente' };
const STRUM = [
  { k: 'n456', l: '456' }, { k: 'copertura', l: 'Cop.' }, { k: 'lucchetto', l: 'Lucch.' },
  { k: 'dominio', l: 'Dom.' }, { k: 'indaga', l: 'Indaga' }, { k: 'vodafone', l: 'VF' },
];
const COL = { staff: '#14746f', link: '#1c5f93', agente: '#b8531f' };
const ORD_OPZ = [['verifiche', 'Più attivi'], ['accessi', 'Accessi'], ['ultima', 'Ultima att.']];

function fmtGiorni(g) {
  if (g == null) return '—';
  if (g === 0) return 'oggi';
  if (g === 1) return 'ieri';
  return `${g} gg fa`;
}
const nf = (n) => (n ?? 0).toLocaleString('it-IT');
const tfmt = (d) => (d ? `${d.slice(8)}/${d.slice(5, 7)}` : '');

const chartTick = { fontSize: 9, fill: 'var(--muted)' };
const chartTooltip = { fontSize: 11, borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--line)', color: 'var(--ink)' };

export default function UtilizzoPage() {
  const [giorni, setGiorni] = useState(30);
  const [data, setData] = useState(null);
  const [and, setAnd] = useState([]);
  const [tipo, setTipo] = useState('tutti');
  const [ord, setOrd] = useState('verifiche');
  const [sel, setSel] = useState(null);   // { tipo, id, nome }
  const [drill, setDrill] = useState(null);
  const drillReq = useRef(0);   // guardia anti-race su tap rapidi

  useEffect(() => {
    axios.get(`${API}/utilizzo/riepilogo?giorni=${giorni}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [giorni]);

  useEffect(() => {
    axios.get(`${API}/utilizzo/andamento?giorni=${giorni}`)
      .then((r) => setAnd(r.data?.giorni || [])).catch(() => setAnd([]));
  }, [giorni]);

  const apri = (u) => {
    const my = ++drillReq.current;
    setSel(u); setDrill(null);
    axios.get(`${API}/utilizzo/utente/${u.tipo}/${encodeURIComponent(u.id)}?giorni=${giorni}`)
      .then((r) => { if (my === drillReq.current) setDrill(r.data); })
      .catch(() => { if (my === drillReq.current) setDrill({ errore: true }); });
  };

  const tot = data?.totali || {};
  const utenti = (data?.utenti || [])
    .filter((u) => tipo === 'tutti' || u.tipo === tipo)
    .slice()
    .sort((a, b) => {
      if (ord === 'accessi') return (b.accessi || 0) - (a.accessi || 0);
      if (ord === 'ultima') return (b.ultima || '').localeCompare(a.ultima || '');
      return b.verifiche - a.verifiche;
    });

  return (
    <>
      <div className="m-seg2">
        {PERIODI.map((g) => (
          <button key={g} className={giorni === g ? 'on' : ''} onClick={() => setGiorni(g)}>{g} giorni</button>
        ))}
      </div>

      <div className="m-chips" style={{ marginBottom: 14 }}>
        <div className="m-chip"><div className="cn">{tot.utenti_attivi ?? '—'}</div><div className="cl">Attivi</div></div>
        <div className="m-chip"><div className="cn">{nf(tot.verifiche)}</div><div className="cl">Verifiche</div></div>
        <div className="m-chip"><div className="cn">{tot.inattivi ?? '—'}</div><div className="cl">Inattivi</div></div>
        <div className="m-chip"><div className="cn">{tot.senza_attivita ?? '—'}</div><div className="cl">Senza attività</div></div>
      </div>

      <div className="mu-chart">
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={and} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
            <XAxis dataKey="data" tickFormatter={tfmt} tick={chartTick} interval="preserveStartEnd" axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={chartTick} width={26} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={chartTooltip} labelFormatter={(d) => `Giorno ${d}`} />
            <Bar dataKey="staff" name="Staff" stackId="a" fill={COL.staff} />
            <Bar dataKey="link" name="Link" stackId="a" fill={COL.link} />
            <Bar dataKey="agente" name="Agente" stackId="a" fill={COL.agente} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="m-seg2" style={{ marginTop: 2 }}>
        {['tutti', 'staff', 'link', 'agente'].map((t) => (
          <button key={t} className={tipo === t ? 'on' : ''} onClick={() => setTipo(t)}>{t === 'tutti' ? 'Tutti' : TIPO_LABEL[t]}</button>
        ))}
      </div>
      <div className="m-seg2">
        {ORD_OPZ.map(([k, l]) => (
          <button key={k} className={ord === k ? 'on' : ''} onClick={() => setOrd(k)}>{l}</button>
        ))}
      </div>

      {!data ? (
        <div className="m-empty">Caricamento…</div>
      ) : utenti.length === 0 ? (
        <div className="m-empty">Nessun utente nel periodo.</div>
      ) : (
        <div className="mu-users">
          {utenti.map((u) => (
            <div key={`${u.tipo}-${u.id}`} className={`mu-row${u.inattivo ? ' off' : ''}`} onClick={() => apri(u)}>
              <div className="mu-row-top">
                <span className="mu-name">{u.nome}</span>
                <span className={`mu-badge ${u.tipo}`}>{TIPO_LABEL[u.tipo] || u.tipo}</span>
              </div>
              <div className="mu-row-bottom">
                <span className="mu-metric gold">{nf(u.verifiche)}<i>verifiche</i></span>
                <span className="mu-metric">{u.accessi || 0}<i>accessi</i></span>
                <span className="mu-metric">{fmtGiorni(u.inattivo_da_giorni)}<i>ultima att.</i></span>
                <span className="mu-strum">
                  {STRUM.filter((s) => u.per_strumento?.[s.k]).map((s) => (
                    <span key={s.k} className="mu-chip2">{s.l} {u.per_strumento[s.k]}</span>
                  ))}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {sel && (
        <div className="mu-scrim" onClick={() => setSel(null)}>
          <div className="mu-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="mu-sheet-h">
              <div><span className={`mu-badge ${sel.tipo}`}>{TIPO_LABEL[sel.tipo] || sel.tipo}</span><b>{sel.nome}</b></div>
              <button className="mu-x" onClick={() => setSel(null)} aria-label="Chiudi">×</button>
            </div>
            <div className="mu-sheet-body">
              {!drill ? (
                <div className="m-empty">Caricamento…</div>
              ) : drill.errore ? (
                <div className="m-empty">Errore nel caricamento.</div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={drill.serie} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                      <XAxis dataKey="data" tickFormatter={tfmt} tick={chartTick} interval="preserveStartEnd" axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={chartTick} width={24} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={chartTooltip} />
                      <Bar dataKey="n" name="Verifiche" fill="var(--signal)" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>

                  <div className="mu-tiles">
                    {STRUM.map((s) => (
                      <div key={s.k} className="mu-tile"><div className="tv">{drill.per_strumento?.[s.k] || 0}</div><div className="tl">{s.l}</div></div>
                    ))}
                  </div>

                  <div className="m-secrow"><h2>Ultimi accessi</h2></div>
                  {(drill.accessi || []).length === 0 ? (
                    <div className="m-empty" style={{ padding: '14px 4px' }}>Nessun accesso registrato.</div>
                  ) : (
                    <div className="mu-list">
                      {drill.accessi.map((a, i) => (
                        <div key={i} className="mu-li">
                          <span className={a.esito === 'ok' ? 'ok' : 'ko'}>{a.esito === 'ok' ? '✓' : '✕'}</span>
                          <span>{(a.ts || '').slice(0, 16).replace('T', ' ')}</span>
                          <span className="mu-li-sub">{a.device || a.ip || '—'}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="m-secrow"><h2>Ultime verifiche</h2></div>
                  {(drill.ultime || []).length === 0 ? (
                    <div className="m-empty" style={{ padding: '14px 4px' }}>Nessuna verifica.</div>
                  ) : (
                    <div className="mu-list">
                      {drill.ultime.map((u2, i) => (
                        <div key={i} className="mu-li">
                          <b>{u2.tipo}</b>
                          <span>{(u2.at || '').slice(0, 16).replace('T', ' ')}</span>
                          <span className="mu-li-sub">{u2.esito || '—'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
